import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Flag } from '@mui/icons-material';
import { toast } from 'react-toastify';
import productService from '../../services/productService';
import {
  PRODUCT_REPORT_REASONS,
  OTHER_REPORT_REASON,
  MAX_REPORT_REASON_LENGTH,
} from '../../utils/reportReasons';

const ReportProductButton = ({ productId }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOther = selectedReason === OTHER_REPORT_REASON;
  const reason = isOther ? customReason.trim() : selectedReason;

  const handleOpen = () => {
    if (!user) {
      toast.error('Please login to report a product');
      navigate('/login', { state: { from: { pathname: `/products/${productId}` } } });
      return;
    }
    setSelectedReason('');
    setCustomReason('');
    setOpen(true);
  };

  const handleClose = () => {
    if (!submitting) setOpen(false);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!reason) {
      toast.error(isOther ? 'Please describe the reason for reporting' : 'Please select a reason for reporting');
      return;
    }

    setSubmitting(true);
    try {
      await productService.reportProduct(productId, reason);
      setOpen(false);
      toast.success('Report submitted successfully');
    } catch (error) {
      toast.error(error?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<Flag />}
          onClick={handleOpen}
        >
          Report
        </Button>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Report Product</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Why are you reporting this product?
          </Typography>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
          >
            {PRODUCT_REPORT_REASONS.map((r) => (
              <FormControlLabel
                key={r}
                value={r}
                control={<Radio size="small" />}
                label={r}
                disabled={submitting}
              />
            ))}
          </RadioGroup>
          {isOther && (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              margin="dense"
              label="Describe the issue"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              disabled={submitting}
              inputProps={{ maxLength: MAX_REPORT_REASON_LENGTH }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmit}
            disabled={!reason || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportProductButton;
